import React, { useMemo } from 'react';
import { LISTING_STATE_CLOSED } from '../../../util/types';
import css from './RenderMobileInformationMaybe.module.css';
import { FormattedMessage } from 'react-intl';
import { formatMoneyIfSupportedCurrency } from '../../../components/OrderPanel/OrderPanel';
import { priceData } from '../ListingPage.shared';
import { displayPrice } from '../../../util/configHelpers';
import { AvatarSmall, H1, H2, Icons } from '../../../components';
import { userDisplayNameAsString } from '../../../util/data';
import { getListingPriceTotals } from '../../../util/pricing';
import { isNewCondition } from '../../../util/genericHelpers';
import ProductSizeHighlight from '../ProductSizeHighlight';

function PriceMaybe({
  publicData,
  price,
  validListingTypes,
  intl,
  marketplaceCurrency,
  showCurrencyMismatch,
}) {
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = useMemo(
    () => validListingTypes.find(conf => conf.listingType === listingType),
    [validListingTypes, listingType]
  );

  const showPrice = displayPrice(foundListingTypeConfig);
  const isPriceVariationsInUse = !!publicData?.priceVariationsEnabled;
  const hasMultiplePriceVariants = publicData?.priceVariants?.length > 1;

  if (!showPrice || !price || (isPriceVariationsInUse && hasMultiplePriceVariants)) {
    return null;
  }

  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  const priceValue = (
    <span className={css.priceValue}>{formatMoneyIfSupportedCurrency(price, intl)}</span>
  );

  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValueInCTA} title={priceTitle}>
        <FormattedMessage
          id="OrderPanel.priceInMobileCTA"
          values={{ priceValue: formattedPrice }}
        />
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>
        <FormattedMessage
          id="OrderPanel.price"
          values={{
            priceValue,
            pricePerUnit: (
              <span className={css.perUnit}>
                <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
              </span>
            ),
          }}
        />
      </p>
    </div>
  );
}

function RenderMobileInformationMaybe({
  listing,
  title,
  titleDesktop,
  intl,
  titleClasses,
  validListingTypes,
  marketplaceCurrency,
  showCurrencyMismatch = false,
  config,
  authorBlock,
}) {
  const description = listing?.attributes?.description;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const subTitleText = isClosed
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const publicData = listing?.attributes?.publicData || {};

  const { total: price } = getListingPriceTotals(listing, config);
  const isNewProduct = isNewCondition(publicData?.productcondition);

  return (
    <div>
      <div className={css.modalHeading}>
        <H1 className={css.heading}>{title}</H1>
      </div>

      <div className={css.orderHeading}>
        {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
        {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
      </div>

      <PriceMaybe
        publicData={publicData}
        price={price}
        validListingTypes={validListingTypes}
        intl={intl}
        marketplaceCurrency={marketplaceCurrency}
        showCurrencyMismatch={showCurrencyMismatch}
      />
      <ProductSizeHighlight publicData={publicData} />

      <div className={css.conditionContainer}>
        {/* {isNewProduct ? (
          <div className={css.conditionNew}>
            <FormattedMessage id="OrderPanel.conditionNew" />
          </div>
        ) : null} */}
        {description && <div className={css.description}>{description}</div>}
      </div>
      {/* Trust elements / benefits */}
      <div className={css.purchaseBenifits}>
        <div className={css.benefit}>
          <Icons name="protection" />
          <span>
            <FormattedMessage id="RenderMobileInformationMaybe.benefitBuyerProtection" />
          </span>
        </div>
        <div className={css.benefit}>
          <Icons name="payLater" />
          <span>
            <FormattedMessage id="RenderMobileInformationMaybe.benefitSecurePayments" />
          </span>
        </div>
        <div className={css.benefit}>
          <Icons name="delivery" />
          <span>
            <FormattedMessage id="RenderMobileInformationMaybe.benefitTrackedShipping" />
          </span>
        </div>
        <div className={css.benefit}>
          <Icons name="lock" />
          <span>
            <FormattedMessage id="RenderMobileInformationMaybe.escrowHoldExplainer" />
          </span>
        </div>
      </div>

      {authorBlock}
    </div>
  );
}

export default RenderMobileInformationMaybe;
